import { TagValidationUtils } from 'tag/ts/smart_tag/operations/types/TagValidationUtils';
import { TTagResetData } from 'tag/ts/smart_tag/types/api/TTagResetData';
import { TTagResetReject } from 'tag/ts/smart_tag/types/api/TTagResetReject';
import { TTagResetResolve } from 'tag/ts/smart_tag/types/api/TTagResetResolve';
import { ITag } from 'tag/ts/tag/interfaces/ITag';
import { unregisterAviaErrorHandler } from 'tag/ts/tag/player/adapters/avia/utilities/AviaErrorHandler';
import { IResetPromiseResult, ISmartTagOperationsDependencies } from './types/SmartTagOperationsTypes';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Handles batch tag reset operations with concurrent processing and global resource management
 *
 * **BATCH RESET WITH GLOBAL CLEANUP STRATEGY:**
 * Implements efficient multi-tag reset using Promise.all for concurrent processing,
 * with sophisticated global resource management to prevent memory leaks while
 * maintaining functional correctness.
 *
 * **Global Resource Management Logic:**
 * - **Complete Success**: All tags reset → Safe to clean up global resources (unregisterAviaErrorHandler)
 * - **Partial Failure**: Some tags failed → Keep global resources active for failed tags
 * - **Complete Failure**: All tags failed → Preserve all state and global resources
 *
 * **Memory Leak Prevention:**
 * Only removes global event listeners when guaranteed that NO Avia players remain
 * active in the system. This prevents both memory leaks (from orphaned listeners)
 * and functional issues (console error spam from removing handlers too early).
 *
 * Provides comprehensive error handling and partial success reporting for monitoring.
 */
export class SmartTagResetOperations {
	#deps: ISmartTagOperationsDependencies;

	constructor( deps: ISmartTagOperationsDependencies ) {
		this.#deps = deps;
	}

	/**
	 * @description Reset all tags with batch processing
	 * @return {Promise<TTagResetData>}
	 */
	resetTags = (): Promise<TTagResetData> => {
		this.#deps.logger.log( 'reset:' );

		return new Promise( async ( resolve: TTagResetResolve, reject: TTagResetReject ) => {
			const tagsToReset = this.#getTagsToReset();

			if ( tagsToReset.length === 0 ) {
				this.#cleanupEmptyRegistry();
				resolve( { msg: 'Success: no tags to remove.' } );
				return;
			}

			await this.#resetTagsBatch( tagsToReset, resolve, reject );
		} );
	};

	/**
	 * @description Safely extracts tags for reset operations preventing race conditions
	 * Caches registry reference to prevent inconsistencies between registry access calls
	 * during concurrent operations.
	 */
	#getTagsToReset(): Array<{ key: string; tag: ITag }> {
		const registry = this.#deps.getRegistry(); // Get once to prevent race conditions
		return Object.keys( registry ).map( ( key ) => ( {
			key,
			tag: registry[ key ] // Use cached registry
		} ) );
	}

	/**
	 * @description Performs complete cleanup when registry becomes empty
	 *
	 * **GLOBAL CLEANUP TRIGGER POINT:**
	 * This method is ONLY called when ALL tags have been successfully reset/removed,
	 * ensuring the system is in a clean state with no active Avia players.
	 *
	 * **Memory Leak Prevention:**
	 * - Clears Smart Tag collections (registry & list)
	 * - Removes global AviaErrorHandler event listener
	 * - Prevents accumulation of orphaned global resources
	 *
	 * **Why Global Cleanup Happens Here:**
	 * 1. **Safety Guarantee**: Only called after ALL tags successfully reset
	 * 2. **No Active Players**: Guaranteed no Avia players remain running
	 * 3. **Error Handler Not Needed**: No more Avia internal errors possible
	 * 4. **Memory Leak Prevention**: Global event listeners prevent garbage collection
	 *
	 * **Critical for SPA Applications:**
	 * Single-page applications that create/destroy Smart Tag instances repeatedly
	 * will accumulate global event listeners without this cleanup, leading to
	 * progressive memory leaks and performance degradation.
	 */
	#cleanupEmptyRegistry(): void {
		// Clear Smart Tag tracking collections
		this.#deps.setList( [] );
		this.#deps.setRegistry( {} );

		// **GLOBAL MEMORY LEAK PREVENTION:**
		// Remove global unhandledrejection event listener to prevent memory accumulation
		// Safe to remove because:
		// - All tags successfully reset (guaranteed by caller logic)
		// - No active Avia player instances remain
		// - No more Avia internal promise rejections possible
		// - Prevents global event listener from keeping references alive indefinitely
		unregisterAviaErrorHandler();

		// Tear down the global connection monitor: removes its online/offline window listeners
		// and clears the Firefox polling interval, which otherwise outlive the reset. Cleared so a
		// subsequent create() re-initializes it for the next tag.
		this.#deps.getConnectionMonitor()?.destroy();
		this.#deps.setConnectionMonitor( undefined );

		this.#deps.logger.log( 'reset:', 'Global event listeners cleaned up - system reset to clean state' );
	}

	/**
	 * @description Batch processing approach to handle multiple tag resets efficiently
	 * Uses Promise.all to process all tags concurrently rather than sequentially,
	 * improving performance and providing comprehensive error reporting for monitoring.
	 */
	async #resetTagsBatch( tagsToReset: Array<{ key: string; tag: ITag }>, resolve: TTagResetResolve, reject: TTagResetReject ): Promise<void> {
		try {
			const resetPromises = tagsToReset.map( ( { key, tag } ) => this.#createResetPromise( key, tag ) );
			const results = await Promise.all( resetPromises );
			this.#processResetResults( results, resolve );
		} catch ( error ) {
			this.#deps.logger.error( 'reset:', 'Unexpected error in Promise.all:', error );
			reject( { msg: 'Failure: unexpected error during reset.', error } );
		}
	}

	/**
	 * @description Creates individual reset promises with comprehensive error handling
	 * Validates tag structure before attempting reset and provides structured error responses
	 * for debugging and monitoring purposes. Ensures no unhandled promise rejections.
	 */
	async #createResetPromise( key: string, tag: ITag ): Promise<IResetPromiseResult> {
		if ( !TagValidationUtils.validateTagForReset( tag, this.#deps.utilities ) ) {
			return {
				success: false,
				key,
				tag,
				error: 'Tag reset method not available'
			};
		}

		try {
			const data = await tag.API.TAG.reset();
			this.#deps.logger.log( 'reset:', 'Tag.reset():', data );
			return {
				success: true,
				key,
				tag,
				data
			};
		} catch ( error ) {
			this.#deps.logger.warn( 'reset:', 'Tag.reset() failed:', error );
			return {
				success: false,
				key,
				tag,
				error
			};
		}
	}

	/**
	 * @description Processes reset results with partial success handling and global resource management
	 *
	 * **CRITICAL MEMORY MANAGEMENT LOGIC:**
	 * This method implements sophisticated cleanup logic that must balance memory leak prevention
	 * with functional correctness. The global AviaErrorHandler can only be safely removed when
	 * NO active Avia players remain that might generate errors.
	 *
	 * **Use Cases:**
	 *
	 * 1. **Complete Success (All tags reset successfully)**
	 *    - Registry becomes empty
	 *    - No active Avia players remain
	 *    - Safe to remove global error handler (prevents memory leak)
	 *    - Action: Call #cleanupEmptyRegistry() → unregisterAviaErrorHandler()
	 *
	 * 2. **Partial Failure (Some tags fail to reset)**
	 *    - Failed tags remain active with Avia players
	 *    - Avia players may still generate internal errors
	 *    - Global error handler MUST remain active (prevents console noise)
	 *    - Action: Keep handler active, preserve failed tags in registry
	 *
	 * 3. **Complete Failure (All tags fail to reset)**
	 *    - All tags remain active
	 *    - Registry unchanged
	 *    - Global error handler remains essential
	 *    - Action: No cleanup, full preservation of state
	 *
	 * **Why This Logic is Critical:**
	 * - AviaErrorHandler is GLOBAL and suppresses Avia library race condition errors
	 * - Removing it while Avia players are active causes console error spam
	 * - Keeping it when no players exist causes memory leaks
	 * - Failed reset attempts mean tags/players are still potentially active
	 *
	 * Ensures partial failures don't cause complete operation failure, providing
	 * detailed feedback for both successful and failed operations to support
	 * debugging and system monitoring requirements.
	 */
	#processResetResults( results: IResetPromiseResult[], resolve: TTagResetResolve ): void {
		const successfulResets = [];
		const failedResets = [];

		// Separate successful from failed reset operations
		results.forEach( ( result ) => {
			if ( result.success ) {
				this.#removeTagFromCollections( result.tag );
				successfulResets.push( result );
			} else {
				failedResets.push( result );
			}
		} );

		// **GLOBAL RESOURCE CLEANUP DECISION POINT**
		// Only cleanup global resources if ALL tags were successfully reset
		// If any failed, they might still be active and need the global error handler
		if ( failedResets.length === 0 ) {
			// **USE CASE 1: Complete Success**
			// - All tags successfully reset and destroyed
			// - No active Avia players remain in the system
			// - Safe to remove global event listeners (prevents memory leaks)
			// - Registry and list can be completely cleared
			this.#cleanupEmptyRegistry(); // Calls unregisterAviaErrorHandler()
		} else {
			// **USE CASE 2 & 3: Partial or Complete Failure**
			// - Some/all tags failed to reset properly
			// - Failed tags likely still have active Avia player instances
			// - Global AviaErrorHandler MUST remain active to suppress Avia internal errors
			// - Registry preserved with failed tags for potential retry operations
			this.#deps.logger.log( 'reset:', `Partial reset: ${successfulResets.length} successful, ${failedResets.length} failed - keeping global handlers active` );

			// **IMPORTANT:** Don't clear registry/list - failed tags might still be active
			// Failed tags remain tracked in registry for:
			// - Potential manual cleanup retry
			// - Debugging failed reset operations
			// - Ensuring global handlers stay active while needed
		}

		this.#resolveResetResults( successfulResets, failedResets, resolve );
	}

	/**
	 * @description Safe collection removal with utility validation
	 * Implements defensive programming pattern to handle cases where
	 * array utilities or list structures may be unavailable at runtime.
	 * Ensures registry cleanup always occurs even if list cleanup fails.
	 */
	#removeTagFromCollections( tag: ITag ): void {
		if ( !this.#deps.utilities?.Array?.indexOf ) {
			this.#deps.logger.warn( 'removeTagFromCollections:', 'Array utilities not available' );
			// Still remove from registry even if list cleanup fails
			this.#deps.removeFromRegistry( tag.uuid );
			return;
		}

		const list = this.#deps.getList();
		if ( list ) {
			const indexToDelete = this.#deps.utilities.Array.indexOf( 'uuid', tag.uuid, list );
			if ( indexToDelete !== null && indexToDelete !== undefined ) {
				this.#deps.removeFromList( tag.uuid );
			}
		}

		this.#deps.removeFromRegistry( tag.uuid );
	}

	/**
	 * @description Formats and resolves reset operation results with comprehensive reporting
	 * Provides detailed success/failure metrics for monitoring and debugging while
	 * ensuring appropriate resolution messages based on operation outcomes.
	 */
	#resolveResetResults( successfulResets: IResetPromiseResult[], failedResets: IResetPromiseResult[], resolve: TTagResetResolve ): void {
		this.#deps.logger.log(
			'reset:',
			`Completed: ${successfulResets.length} successful, ${failedResets.length} failed`
		);

		if ( failedResets.length === 0 ) {
			resolve( {
				msg: 'Success: all tags removed.',
				data: { successful: successfulResets.length }
			} );
		} else {
			resolve( {
				msg: `Partial success: ${successfulResets.length} removed, ${failedResets.length} failed.`,
				data: {
					successful: successfulResets.length,
					failed: failedResets.length,
					errors: failedResets.map( ( r ) => r.error )
				}
			} );
		}
	}
}